import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import styled from 'styled-components';
import { useAlternatePaymentMethodsMutation } from '@syf-api-library/alternate-payment-methods';
import { usePaymentMethodsMutation } from '@syf-api-library/payment-methods';
import { spacing } from 'syf-component-library/defaultTheme';
import Textfield from 'syf-component-library/ui/atoms/Textfield';
import { Grid } from 'syf-component-library/ui/patterns';
import { Inline, Inset, Stack } from 'syf-component-library/ui/spacing';
import getLanguageFromStorage from 'syf-js-utilities/helpers/getLanguageFromStorage';
import { PAGE_FUNCTION, PAGE_NAME, PaymentMethodToPageSubFunctionText, SUB_FUNCTION } from '../../../../const/analytics';
import { routingAccountNumbers } from '../../../../const/assets';
import { CHECKING, SAVINGS } from '../../../../const/paymentMedium';
import MainContext from '../../../../Context';
import { isMonthly, isPayLaterProduct } from '../../../../helpers';
import { getErrorMessage, getPaymentTypeError } from '../../../../helpers/getPaymentMethodErrorMessage';
import { useApiConfig } from '../../../../hooks';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import usePaymentMethodMachine from '../../../../hooks/machines/usePaymentMethodMachine';
import { ACCOUNT_NUMBER_INPUT_NAME } from '../../../../machines/paymentMethod/bankForm/accountNumberConfig';
import { NICKNAME_INPUT_NAME } from '../../../../machines/paymentMethod/bankForm/nicknameConfig';
import { ROUTING_NUMBER_INPUT_NAME } from '../../../../machines/paymentMethod/bankForm/routingNumberConfig';
import { t } from '../../../../strings/i18n';
import { useField, useXForm, XForm } from '../../../../xform';
import { ErrorIcon } from '../../../atoms/ErrorIcon';
import { Image, ImageContainer } from '../../../atoms/styled';
const NickNameInstruction = styled(Inset) `
  padding: ${spacing.large}px 0px ${spacing.small}px;
`;
const Form = ({ shouldShowNickname }) => {
    const { submit } = useXForm();
    const routingNumber = useField(ROUTING_NUMBER_INPUT_NAME);
    const accountNumber = useField(ACCOUNT_NUMBER_INPUT_NAME);
    const nickname = useField(NICKNAME_INPUT_NAME);
    const handleSubmit = (e) => {
        e.preventDefault();
        submit();
    };
    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        // to avoid typing of spaces in the beginning
        const trimmedValue = inputValue === null || inputValue === void 0 ? void 0 : inputValue.trimStart();
        nickname.setValue(trimmedValue);
    };
    const handleBlur = (nicknameField) => {
        var _a;
        // to remove any leading white space
        const trimmedValue = (_a = nicknameField === null || nicknameField === void 0 ? void 0 : nicknameField.value) === null || _a === void 0 ? void 0 : _a.trim();
        nickname.setValue(trimmedValue);
        nickname.blur();
    };
    return (_jsxs("form", Object.assign({ onSubmit: handleSubmit }, { children: [_jsx("span", { "data-scroll": ROUTING_NUMBER_INPUT_NAME }), _jsx(Textfield, { "data-private": true, dataCy: "add-payment-method-routing-number", error: routingNumber.error, errorIcon: _jsx(ErrorIcon, {}), id: "routingNumber", inputMode: "numeric", matchPattern: /^([0-9]*)$/, name: "routingNumber", placeholder: t('addPaymentMethod.routingNumber'), required: true, type: "text", value: routingNumber.value, width: "100%", onBlur: routingNumber.blur, onChange: (e) => routingNumber.setValue(e.target.value) }), _jsx(Stack, { size: "large" }), _jsx("span", { "data-scroll": ACCOUNT_NUMBER_INPUT_NAME }), _jsx(Textfield, { "data-private": true, dataCy: "add-payment-method-account-number", error: accountNumber.error, errorIcon: _jsx(ErrorIcon, {}), id: "accountNumber", inputMode: "numeric", matchPattern: /^([0-9]*)$/, name: "accountNumber", placeholder: t('addPaymentMethod.accountNumber'), required: true, type: "text", value: accountNumber.value, width: "100%", onBlur: accountNumber.blur, onChange: (e) => accountNumber.setValue(e.target.value) }), shouldShowNickname && (_jsxs(_Fragment, { children: [_jsx(NickNameInstruction, Object.assign({ "data-cy": "nickname-instruction-text" }, { children: t('addPaymentMethod.nickNameInstruction') })), _jsx("span", { "data-scroll": NICKNAME_INPUT_NAME }), _jsx(Textfield, { "data-private": true, dataCy: "add-payment-method-account-nickname", error: nickname.error, errorIcon: _jsx(ErrorIcon, {}), helpText: t('addPaymentMethod.nickNameHelpText'), id: "nickname", matchPattern: /^[a-zA-Z0-9 ]{0,32}$/, maxLength: 32, name: "nickname", placeholder: t('addPaymentMethod.accountNickname'), type: "text", value: nickname.value, width: "100%", onBlur: () => handleBlur(nickname), onChange: handleInputChange })] })), _jsx("input", { "aria-hidden": true, "data-testid": "submit-button", style: { display: 'none' }, type: "submit" })] })));
};
const CheckingSavingsAccountForm = ({ className, setFormErrorMessage, setIsSubmitting }) => {
    const { sendToMainMachine, staticHostName, triggerAnalyticsEvent, featureFlags: { isPLAddPaymentMethodsEnabled } } = useContext(MainContext);
    const [paymentMethodMachine] = usePaymentMethodMachine();
    const [apiMachine] = useApiMachine();
    const [, , sendToPaymentFlowMachine] = usePaymentFlowMachine();
    const { accountSummaryQueryResult } = apiMachine.context;
    const apiConfig = useApiConfig();
    const { data: accountSummary } = accountSummaryQueryResult;
    const productType = accountSummary.product.type;
    const paymentMethodMutation = usePaymentMethodsMutation(apiConfig);
    const alternatePaymentMethodMutation = useAlternatePaymentMethodsMutation(apiConfig);
    const mutation = isPLAddPaymentMethodsEnabled || !isPayLaterProduct(productType)
        ? paymentMethodMutation
        : alternatePaymentMethodMutation;
    // TODO: Remove legacyChecking when we deprecate the isManagePaymentMethodsEnabled featureFlag
    const legacyChecking = paymentMethodMachine.matches('addNewCheckingAccount');
    const isChecking = legacyChecking ||
        paymentMethodMachine.matches('addPaymentMethod.addNewCheckingAccount');
    // END TODO
    const paymentMethodType = isChecking ? CHECKING : SAVINGS;
    const onInvalidHandler = (formErrors) => {
        const errorMessage = [
            [formErrors.routingNumber, 'Routing-Number'],
            [formErrors.accountNumber, 'Account-Number'],
            [formErrors.nickname, 'Nickname']
        ]
            .filter(([value]) => Boolean(value))
            .map(([value, key]) => `${key}: ${value}`)
            .join(' ');
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: SUB_FUNCTION.PAYMENT_METHOD.ADD_PAYMENT_METHOD,
            pageName: isChecking
                ? PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHOD_CHECKING_ERROR
                : PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHOD_SAVINGS_ERROR,
            errorMessage
        });
    };
    // TODO: the `submitHandler` logic in this form should be moved to the payment method machines. It is currently copy and pasted in payment method forms
    const submitHandler = async (paymentMethod) => {
        setIsSubmitting(true);
        try {
            const { accountNumber, nickname, routingNumber } = paymentMethod;
            const newPaymentMethod = {
                paymentMedium: 'E_CHECK',
                paymentMethod: {
                    'cipher.bankAccountNumber': accountNumber,
                    'cipher.bankRoutingNumber': routingNumber,
                    nickname,
                    type: paymentMethodType
                }
            };
            const { paymentMethod: { id: paymentMethodId } } = await mutation.mutateAsync(newPaymentMethod);
            sendToPaymentFlowMachine({
                type: 'SET_NEW_PAYMENT_METHOD',
                paymentMethodId
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const onSubmitErrorHandler = (error) => {
        const response = error === null || error === void 0 ? void 0 : error.response;
        if (response) {
            getErrorMessage(response, paymentMethodType).then((errorMessage) => {
                setFormErrorMessage(errorMessage);
                const paymentTypeError = getPaymentTypeError(errorMessage);
                let pageName = 'error';
                switch (paymentTypeError) {
                    case CHECKING:
                        pageName =
                            PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHOD_CHECKING_ERROR;
                        break;
                    case SAVINGS:
                        pageName = PAGE_NAME.PAYMENT_METHOD.ADD_PAYMENT_METHOD_SAVINGS_ERROR;
                        break;
                    default:
                        break;
                }
                triggerAnalyticsEvent({
                    pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                    pageSubFunction: SUB_FUNCTION.PAYMENT_METHOD.ADD_PAYMENT_METHOD,
                    pageName,
                    errorMessage
                });
            });
        }
    };
    const onSubmitHandler = () => {
        sendToMainMachine({
            type: 'GO_TO_PAYMENT_FLOW'
        });
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: PaymentMethodToPageSubFunctionText[paymentMethodType],
            pageName: PAGE_NAME.COMPLETE
        });
    };
    return (_jsxs(Grid, Object.assign({ className: className }, { children: [_jsxs(Grid, Object.assign({ item: true, md: 6, xs: 12 }, { children: [_jsx(XForm, Object.assign({ formMachine: paymentMethodMachine.context.form, submit: submitHandler, onInvalid: onInvalidHandler, onSubmit: onSubmitHandler, onSubmitError: onSubmitErrorHandler }, { children: _jsx(Form, { shouldShowNickname: !isMonthly(accountSummary.product.type) }) })), _jsx(Stack, { size: "xlarge" })] })), _jsxs(ImageContainer, Object.assign({ item: true, md: 6, xs: 12 }, { children: [_jsx(Inline, { hideOnBreakpointDown: "medium" }), _jsxs(Grid, { children: [_jsx(Image, { alt: t('addPaymentMethod.imageAlt'), src: `${staticHostName}${routingAccountNumbers(getLanguageFromStorage())}` }), _jsx(Stack, { hideOnBreakpointUp: "medium", size: "xlarge" })] })] }))] })));
};
export default CheckingSavingsAccountForm;
